import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { ColumnMode, DatatableComponent, SelectionType } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { AccountingNote } from "src/model/class/general classes/accounting-note";
import { Filter } from "src/components/shared-components/class/general-classes/filter";
import { Column, TableInfo } from "src/components/shared-components/class/general-classes/item-history";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iGenericTable } from "src/model/interface/iGeneric-table";
import { AccountingService } from "src/service/accounting.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { TranslationService } from "src/service/translation.service";

@Component({
    selector: "app-accounting-notes-list",
    templateUrl: "./accounting-notes-list.component.html",
    styleUrls: ["./accounting-notes-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountingNotesListComponent implements OnInit, iEvent, OnChanges, iGenericTable {
  @Input("allowEdit") allowEdit: boolean = true;
  @Input("allowDelete") allowDelete: boolean = true;
  @Input("allowInsert") allowInsert: boolean = true;
  @Input("openInPopup") openInPopup: boolean = false;
  @Output("OnChangeSelected") OnChangeSelected = new EventEmitter<string>();

  @ViewChild("accountingNotesTable", { static: false }) table: DatatableComponent;

  private _logContext: string = "Accounting-notes-list.component";

  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;
  private _intervalTimer: number = 4000;

  public lblFunctionalityNotSupported: string = "Funzionalità non supportata";
  public isFunctionalityNotImplemented: boolean = false;

  public filters: Filter[] = [];

  //#region translate
  public description = "Description";
  public startDate = "";
  public endDate = "";
  public registrationDate = "";
  public amount = "";
  //#endregion

  public maxRowCountInTable = 25;

  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;

  /**Flag value; if the skeleton component MUST be showed, it is set to TRUE, otherwise, to FALSE. */
  public isSpookySkeleton: boolean = false;

  public _eventSubscriptions: Array<Subscription> = null;
  public event: any;

  public accountingNotes: Array<AccountingNote> = new Array<AccountingNote>();
  public selectedAccountingNotes: Array<AccountingNote> =
    new Array<AccountingNote>();

  /**Flag value; if the "reporthtml-list" has been properly initialised from public Observable, it is set to TRUE; otherwise it is set to FALSE.
   *
   * NOTE: Generally, when it is set to FALSE, the page should display the "skeleton" list
   */
  public isAccountingNotesInitialized: boolean = false;

  //public isScheduleLoading: boolean = true;
  public showErrorContent: boolean = false;
  public lblNoContentText: string = "";

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public toolBarColor: string = "primary";

  public dateComparator = (valueA, valueB, rowA, rowB, sortDirection?) =>
    this.dateComparatorLocal(valueA, valueB, rowA, rowB, sortDirection);

  public tableInfo: TableInfo = null;
  public selected: Array<any> = new Array<any>();

  constructor(
    private _accountingNotesService: AccountingService,
    private alertService: AlertService,
    private _translateService: TranslateService,
    private _modalController: ModalController,
    private _networkService: NetworkService,
    public _translationService: TranslationService,
    private _loaderController: LoaderService,
    private _loggerService: LoggerService,
    private _eventService: EventService
  ) { }

  public ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo("", false, SelectionType.multi, this.createArrayColumns(), false, true);
    this.tableInfo.IsListInitialized = false;
    this.tableInfo.RowHeight = "auto";

    /* this.isSpookySkeleton = false;
    this.isAccountingNotesInitialized = false; */

    this.subscribeEvents();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();

    var timer = setTimeout(() => {
      this.handleApiResponseError();
    }, this._intervalTimer);

    this._accountingNotesService.GetAccountingNotes("", "").then(
      (listAccountingNotes: AccountingNote[]) => {
        clearTimeout(timer);
        this.accountingNotes = listAccountingNotes;
        this.isFunctionalityNotImplemented = false;
        this.isAccountingNotesInitialized = true;
        this.showErrorContent = false;
      },
      (error) => {
        clearTimeout(timer);
        this.handlePeculiarApiResponseError(error);
      }
    );

    this._accountingNotesService.Init();

    //this.initiFilterFields();

    //add filters for each column
    this.filters.push({
      Kind: FiltersType.text,
      Name: "description",
      PlaceHolder: "",
      Title: "dictionary.description",
      Values: [],
      InputValue: ""
    });
    this.filters.push({
      Kind: FiltersType.date,
      Name: "startDate",
      PlaceHolder: "",
      Title: "dictionary.start_date",
      Values: [],
      InputValue: ""
    });
    this.filters.push({
      Kind: FiltersType.date,
      Name: "endDate",
      PlaceHolder: "",
      Title: "dictionary.end_date",
      Values: [],
      InputValue: ""
    });
  }

  forceReloadData() {
    this._accountingNotesService.needToInitialize = true;
    this._accountingNotesService.Init();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.table != null) {
      this.recalcTable();
    }
  }

  public ngOnDestroy(): void {
    this._translateSubscription.unsubscribe();

    this._networkServiceSubscription.unsubscribe();

    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        if (this.openInPopup) {
          this._modalController.dismiss();
        }
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  public createItemHistoryList(): void {

  }

  public createArrayColumns(): Column[] {
    return [];
  }

  public createItemHistoryDesktopList(): void {

  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );

          this.accountingNotes = [...this.accountingNotes];
        }
      );
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.description",
        "dictionary.start_date",
        "dictionary.end_date",
        "dictionary.registration_date",
        "dictionary.amount",
        "dictionary.functionalityNotSupported",
      ])
      .subscribe((translation) => {
        this.description = translation["dictionary.description"];
        this.startDate = translation["dictionary.start_date"];
        this.endDate = translation["dictionary.end_date"];
        this.registrationDate = translation["dictionary.registration_date"];
        this.amount = translation["dictionary.amount"];
        this.lblFunctionalityNotSupported =
          translation["dictionary.functionalityNotSupported"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  public onCloseModal() {
    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "onCloseModal()", "onCloseModal");
    this._modalController.dismiss();
  }

  public async onSelectChange($event) {
    this.OnChangeSelected.emit(
      this.selectedAccountingNotes.map((note) => note.id).join(",")
    );
  }

  /**
   * Exec filter
   * @param filterData
   */
  public Filter(filterData: Map<string, string>) {
    let queryString = "";
    filterData.forEach((value, key) => {
      if (queryString.length > 0) queryString += "&";
      queryString += key + "=" + encodeURIComponent(value);
    });

    this._accountingNotesService
      .GetAccountingNotes("", queryString)
      .then((listAccountingNotes: AccountingNote[]) => {
        this.accountingNotes = listAccountingNotes;

        this.isAccountingNotesInitialized = true;
        this.showErrorContent = false;
      });
  }

  public RoundDecimal(value: number, nDecimal: number) {
    return this._translationService.formatDecimal(value, true, nDecimal);
  }

  private initiFilterFields(): void {
    this.tableInfo.Filters = [];
    this.tableInfo.Filters.push(StaticClass.createFilterObject(FiltersType.text, "description", "dictionary.description", "", [], ""));
    this.tableInfo.Filters.push(StaticClass.createFilterObject(FiltersType.date, "startDate", "dictionary.start_date", "", [], ""));
    this.tableInfo.Filters.push(StaticClass.createFilterObject(FiltersType.date, "endDate", "dictionary.end_date", "", [], ""));
  }

  /**
   * Recalculates the size of the table.
   */
  private recalcTable(): void {
    var timer = setTimeout(() => {
      this.table.recalculate();
      clearTimeout(timer);
    }, 300);
  }

  /**
   * Handle errors in api call which require peculiar reactions from the page; this includes cases where the error is propagated through subject.error()
   * @param error
   */
  private async handlePeculiarApiResponseError(error: any): Promise<void> {
    switch (error.status) {
      /*This usually is associated with attempts by the app to require access to server-side features which are NOT currently supported*/
      case ApiErrorType.NOT_FOUND:
        this.isFunctionalityNotImplemented = true;
        await this._loaderController.dismissLoaderWaiting();
        break;

      default:
        this.handleApiResponseError();
        break;
    }
  }

  private async handleApiResponseError(): Promise<void> {

    this._loggerService.log(LogLevel.LOG_ERROR, this._logContext, this._logContext, "handleApiResponseError()", "Error");
    this.showErrorContent = true;

    await this._loaderController.dismissLoaderWaiting();
  }

  private dateComparatorLocal(valueA, valueB, rowA, rowB, sortDirection?) {
    return StaticClass.SortDateFunction(valueA, valueB);
  }

  getRowClass = (row) => {
    return this.currentConnectionType != this.connectionType.NONE ? 'online' : 'offline';
  }
}
